import Util from './util';

// enables promise based xhr
// inspired by https://github.com/cferdinandi/atomic  #version 4.4.0
// converted to ES6 class style
class Request {
    constructor(url, options) {
        // Default settings
        let defaults = {
            method: 'GET',
            username: null,
            password: null,
            data: {},
            queryParams: {},
            headers: {
                // DEPRECATED default content-type (causes issue with FormData that auto sets content type to multipart/form-data for uploads)
                // 'Content-type': 'application/x-www-form-urlencoded'
            },
            responseType: 'text',
            timeout: null,
            withCredentials: false,
            onProgress: null
        };

        // Check browser support
        if (!this.supports()) throw 'This browser is not supported';

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});

        // Make request
        return this.xhr(url);
    }

    supports() {
        return 'XMLHttpRequest' in window && 'JSON' in window && 'Promise' in window;
    }

    parse(req) {
        let result;

        if (this.settings.responseType !== 'text' && this.settings.responseType !== '') {

            // should be in parsed object format
            let data = req.response;

            // IE doesn't parse responses as JSON without the json attribute,
            // even with responseType: 'json'
            if (this.settings.responseType === 'json' && typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch (e) {
                    // Not parseable anyway, don't worry about it
                }
            }

            return { data: data, xhr: req };
        }

        try {
            result = JSON.parse(req.responseText);
        } catch (e) {
            result = req.responseText;
        }
        return { data: result, xhr: req };
    }

    param(obj) {
        // If already a string, or if a FormData,File,Blob object, return it as-is
        if (
            typeof obj === 'string' ||
            Object.prototype.toString.call(obj) === '[object FormData]' ||
            Object.prototype.toString.call(obj) === '[object File]' ||
            Object.prototype.toString.call(obj) === '[object Blob]'
        )
            return obj;

        // If the content-type is set to JSON, stringify the JSON object
        if (
            /application\/json/i.test(this.settings.headers['Content-type']) ||
            Object.prototype.toString.call(obj) === '[object Array]'
        )
            return JSON.stringify(obj);

        // Otherwise, convert object to a serialized string
        let encoded = [];
        for (let prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                encoded.push(encodeURIComponent(prop) + '=' + encodeURIComponent(obj[prop]));
            }
        }
        return encoded.join('&');
    }

    addQueryParams(url){

        if(this.settings.queryParams) {
            let encoded = [];
            for (let prop in this.settings.queryParams) {
                if (this.settings.queryParams.hasOwnProperty(prop)) {
                    encoded.push(prop + '=' + this.settings.queryParams[prop]);
                    // url encoding should be done by browser
                    // encoded.push(encodeURIComponent(prop) + '=' + encodeURIComponent(this.settings.queryParams[prop]));
                }
            }

            if(encoded.length > 0){
                let queryParams = encoded.join('&');

                if(url.indexOf('?') === -1){
                    return url + '?' + queryParams;
                } else {
                    return url + '&' + queryParams;
                }
            }
        }

        // return url without modification
        return url;
    }

    xhr(url) {
        // Create the XHR request
        const request = new XMLHttpRequest();

        // Setup the Promise
        const xhrPromise = new Promise((resolve, reject) => {
            // Setup our listener to process compeleted requests
            request.onreadystatechange = () => {
                // Only run if the request is complete
                if (request.readyState !== 4) return;

                // try parse response in all cases
                let response = this.parse(request);

                // Process the response
                if (request.status >= 200 && request.status < 300) {
                    // resolve successful
                    resolve(response);
                } else {
                    // reject with error
                    reject(response);
                }
            };

            // add query params if defined
            url = this.addQueryParams(url);

            // Setup our HTTP request
            request.open(this.settings.method, url, true, this.settings.username, this.settings.password);
            request.responseType = this.settings.responseType;

            // Add headers
            for (let header in this.settings.headers) {
                if (this.settings.headers.hasOwnProperty(header)) {
                    request.setRequestHeader(header, this.settings.headers[header]);
                }
            }

            // Set timeout
            if (this.settings.timeout) {
                request.timeout = this.settings.timeout;
                request.ontimeout = (e) => {
                    reject({
                        status: 408,
                        statusText: 'Request timeout'
                    });
                };
            }

            // Add withCredentials
            if (this.settings.withCredentials) {
                request.withCredentials = true;
            }

            if (this.settings.onProgress) {
                request.upload.onprogress = this.settings.onProgress;
            }

            // Send the request
            request.send(this.param(this.settings.data));
        });

        // Cancel the XHR request
        xhrPromise.cancel = () => {
            request.abort();
        };

        // Return the request as a Promise
        return xhrPromise;
    }
}

export default Request;
